import React, { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useToasts } from 'react-toast-notifications';
import { useHistory } from 'react-router-dom';
import { Button } from 'rebass';
import { FiArrowRight } from 'react-icons/fi';

import H2 from 'components/H2';
import auth from 'services/auth';
import { initializeEcho, getPrivateUserChannel } from 'utils/echo';
import { useDrawer } from 'context/DrawerContext';
import { buildExportDownloadUrl } from 'utils/exportDownloadUrl';
import { PUSHER_EVENT_EXPORT_READY } from 'utils/constants';

/**
 * Payload broadcast on `private-App.User.{userId}` as the `export.ready` event.
 * Mirrors `App\Events\Exports\ExportReady::broadcastWith()` on the API.
 *
 * @typedef {Object} ExportReadyPayload
 * @property {'study'|'panel'} export_type
 * @property {'completed'|'failed'} status
 * @property {number} export_id ID of the export row itself — the value the
 *     `/download-{study|panel}-export` endpoint expects as `?id=`.
 * @property {number|null} resource_id Study ID for `'study'` exports (used to
 *     navigate to `/studies/{id}`); always null for `'panel'` exports.
 * @property {string} [resource_name] Display name for the resource (e.g. study
 *     title); rendered in the toast title when present.
 * @property {string} [failed_reason] Surfaced under the title when `status === 'failed'`.
 */

/**
 * Inline body for the export.ready BigToast. Mirrors ParticipantsPage's
 * ToastSuccess so the visual treatment matches other big toasts (e.g.,
 * panelist-add). The Download anchor opens in a new tab; "View history" goes
 * through the drawer registry so it works from any route.
 *
 * @param {Object} props
 * @param {ExportReadyPayload} props.payload
 * @param {() => void} props.onViewHistory Click handler for the "View history"
 *     button — typically navigates to the drawer-trigger page and opens the
 *     matching drawer.
 * @param {string} [props.downloadUrl] Resolved absolute download URL. Falsy
 *     hides the Download button (e.g. for failed exports).
 * @returns {JSX.Element}
 */
function ExportReadyToastBody({ payload, onViewHistory, downloadUrl }) {
    const isFailed = payload.status === 'failed';
    const labelKind = payload.export_type === 'study' ? 'study' : 'panel';
    const title = isFailed
        ? `Your ${labelKind} export failed`
        : `Your ${labelKind} export${payload.resource_name ? ` "${payload.resource_name}"` : ''} is ready`;

    return (
        <>
            <H2>{title}</H2>
            {isFailed && payload.failed_reason ? (
                <div className="color-text-secondary fs-body-14 mb-2">{payload.failed_reason}</div>
            ) : null}
            <div className="flex gap-2">
                {!isFailed && downloadUrl ? (
                    <Button
                        as="a"
                        variant="primary"
                        href={downloadUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="!leading-8"
                    >
                        Download
                    </Button>
                ) : null}
                <Button variant="transparent-icon" onClick={onViewHistory}>
                    View history <FiArrowRight />
                </Button>
            </div>
        </>
    );
}

/**
 * Subscribes to the user's broadcast channel for export-ready notifications.
 * Mount once near the App root, INSIDE DrawerProvider so toast actions can
 * reach `useDrawer()` to open the matching drawer.
 *
 * Why this is separate from DrawerProvider:
 *   - Pusher / toast / query-invalidation are unrelated to drawer
 *     orchestration. Splitting them keeps each provider single-purpose and
 *     each replaceable on its own.
 *
 * Why a provider component rather than a top-level hook in App:
 *   - It depends on QueryClient, Toasts, Router, and DrawerContext. Wrapping
 *     it as a provider lets us colocate that scoping at App's tree without
 *     turning App into a hook-only component.
 *
 * @param {Object} props
 * @param {number|string|undefined} props.userId Authenticated user ID. When
 *     unset (logged-out / pre-auth) the subscription effect is skipped.
 * @param {React.ReactNode} props.children
 * @returns {React.ReactNode} `children` unchanged.
 */
export function ExportNotificationsProvider({ userId, children }) {
    const queryClient = useQueryClient();
    const { addToast } = useToasts();
    const history = useHistory();
    const { open: openDrawer } = useDrawer();

    // Stable refs so the subscription effect re-runs only on userId change.
    const openDrawerRef = useRef(openDrawer);
    const historyRef = useRef(history);
    openDrawerRef.current = openDrawer;
    historyRef.current = history;

    useEffect(() => {
        if (!userId) return undefined;
        const pusher = initializeEcho();
        if (!pusher) return undefined;

        const accountId = auth.getAccountId();

        /**
         * @param {ExportReadyPayload} payload
         */
        const handleExportReady = payload => {
            const isStudy = payload.export_type === 'study';

            // Navigate to the page that owns the drawer's trigger before
            // opening it. After the user closes the drawer they land on a page
            // where the same drawer can be re-opened from the UI — no hunting.
            const onViewHistory = () => {
                if (isStudy) {
                    historyRef.current.push(`/studies/${payload.resource_id}`);
                    openDrawerRef.current('studyActivity', {
                        studyId: payload.resource_id,
                        defaultTab: 'exports'
                    });
                } else {
                    historyRef.current.push('/panels');
                    openDrawerRef.current('panelExports');
                }
            };

            addToast(
                <ExportReadyToastBody
                    payload={payload}
                    downloadUrl={buildExportDownloadUrl(isStudy ? 'study' : 'panel', payload.export_id, accountId)}
                    onViewHistory={onViewHistory}
                />,
                {
                    appearance: payload.status === 'failed' ? 'error' : 'success',
                    big: true,
                    autoDismiss: false
                }
            );

            if (isStudy) {
                queryClient.invalidateQueries({ queryKey: ['studyExports', payload.resource_id] });
            } else {
                queryClient.invalidateQueries({ queryKey: ['panelExports'] });
            }
        };

        // On reconnect (network blip, browser sleep) refetch open exports
        // queries so terminal-state transitions during the gap surface without
        // a manual refresh.
        const handleConnected = () => {
            queryClient.invalidateQueries({ queryKey: ['studyExports'] });
            queryClient.invalidateQueries({ queryKey: ['panelExports'] });
        };

        const channel = pusher.subscribe(getPrivateUserChannel(userId));
        channel.bind(PUSHER_EVENT_EXPORT_READY, handleExportReady);
        pusher.connection.bind('connected', handleConnected);

        // Tab refocus catch-up — Pusher may not emit 'connected' if the socket
        // was held open through a short tab-hide.
        window.addEventListener('focus', handleConnected);

        return () => {
            channel.unbind(PUSHER_EVENT_EXPORT_READY, handleExportReady);
            pusher.connection.unbind('connected', handleConnected);
            window.removeEventListener('focus', handleConnected);
        };
    }, [userId, addToast, queryClient]);

    return children;
}
